import { ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import {
  useIsUserManagingAnyCourse,
  useIsUserRole,
  useLogout,
} from "@hooks/auth";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import BackButton from "../Modal/BackButton";
import { DynamicLogo } from "./DynamicLogo";
import LanguageSelector from "./LanguageSelector";
import { ManagementSelector } from "./ManagementSelector";
import NotificationsDrawer from "./NotificationsDrawer";
import ResponsiveMenu from "./ResponsiveMenu";
import ThemeSelector from "./ThemeSelector";

interface HeaderProp {
  children: ReactNode;
  breadcrumbs?: BreadcrumbElement[];
  bottomPadding?: boolean;
  showFooter?: boolean;
  scrollable?: boolean;
  backButtonRoute?: string;
}

export interface BreadcrumbElement {
  name: string;
  link?: string;
}

function Header({
  children,
  breadcrumbs,
  bottomPadding = true,
  showFooter,
  scrollable = true,
  backButtonRoute,
}: HeaderProp) {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = useIsUserRole("admin");
  const isManagingCourse = useIsUserManagingAnyCourse();
  const logout = useLogout();
  const { t } = useTranslation(["header"]);

  const handleHomeClick = () => {
    navigate("/");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy");
  };

  const handleImprintClick = () => {
    navigate("/imprint");
  };

  return (
    <div
      className={`flex flex-col ${scrollable ? "min-h-screen" : "h-[100dvh]"}`}
    >
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <div className="navbar">
          <div className="flex flex-1 items-center justify-between">
            <button
              onClick={handleHomeClick}
              className="btn btn-ghost no-animation flex items-center justify-center gap-0 px-1 md:px-4"
              type="button"
            >
              <DynamicLogo />
            </button>
            <div className="flex items-center gap-1">
              <NotificationsDrawer />
              <LanguageSelector />
              <ThemeSelector />
              {isAdmin && <ManagementSelector />}
              <ResponsiveMenu />
              {auth.user && (
                <button
                  onClick={logout}
                  className="btn btn-ghost btn-sm"
                  type="button"
                >
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t("logout")}</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <div
          className={`mx-3 flex min-h-0 flex-grow flex-col sm:mx-5 md:mx-10 ${
            bottomPadding ? "mb-6" : ""
          }`}
        >
          {breadcrumbs ? (
            <div className="hidden items-center justify-between sm:flex">
              <div className="breadcrumbs p-0">
                <ul className="flex">
                  <li>
                    <button
                      className="link link-hover text-sm sm:text-base"
                      onClick={() => navigate("/")}
                      type="button"
                    >
                      {t("startPage")}
                    </button>
                  </li>
                  {breadcrumbs.map((element) => (
                    <li key={element.name}>
                      {element?.link ? (
                        <button
                          className="link link-hover text-sm sm:text-base"
                          onClick={() => navigate(element.link ?? "")}
                          type="button"
                        >
                          {element.name}
                        </button>
                      ) : (
                        <span className="text-sm sm:text-base">
                          {element.name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-end">
                <div className="mr-sm">
                  <BackButton route={backButtonRoute} />
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6" />
          )}
          <div className="min-h-0 flex-1">{children}</div>
        </div>

        {showFooter && (
          <div className="mb-10 flex flex-col items-center">
            <div className="flex justify-center gap-x-10">
              <button
                onClick={handlePrivacyClick}
                type="button"
                className="link link-hover"
              >
                {t("dataPrivacy")}
              </button>
              <button
                onClick={handleImprintClick}
                type="button"
                className="link link-hover"
              >
                {t("imprint")}
              </button>
              {isManagingCourse && (
                <a
                  href={t("docURL")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link link-hover"
                >
                  {t("documentation")}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Header;
